import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {
    NewEditLocationModalComponent,
    NewEditLocationModalConfig,
    NewEditLocationModalResult
} from '../../modals/new-edit-location-modal/new-edit-location-modal.component';
import {Component, Input} from '@angular/core';
import {ETableColDataType, ETableColFilterType} from '../../../../sdk/src/components/generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {
    CpmCGetLocationsParams,
    CpmCLocationApiService
} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {CpmCLocationApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {CpmCLocationApiUtils} from '../../../../gen/cpm-c-api-ts/src/utils/c-customer/cpmc-location.api-utils';
import {LocationUtils} from '../../utils/location.util';
import {IBaseCRUModalResultBase} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {CpmCLocationDeletePayloadApiSchema} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-location.api-schemas';
import {
    LocationDeletionStrategyModalComponent,
    LocationDeletionStrategyModalConfig,
    LocationDeletionStrategyModalResult
} from '../../modals/location-deletion-strategy-modal/location-deletion-strategy-modal.component';
import {LocationHierarchyTreeComponent} from '../location-hierarchy-tree/location-hierarchy-tree.component';

/**
 * Location CRUD table component
 */
@Component({
    selector: 'app-location-crud',
    templateUrl: './location-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class LocationCRUDComponent extends IBaseCRUDComponent<
    CpmCLocationApiModel,
    NewEditLocationModalComponent,
    NewEditLocationModalConfig,
    NewEditLocationModalResult
> {
    protected helpVisible = false;

    protected _focusParent?: CpmCLocationApiModel;

    // ===============================================================
    // ===============================================================

    public get focusParent(): CpmCLocationApiModel | undefined {
        return this._focusParent;
    }
    @Input()
    public set focusParent(value: CpmCLocationApiModel | undefined) {
        this._focusParent = value;
        this.refresh();
    }

    // ===============================================================
    // ===============================================================

    @Input()
    public fnOverrideOnAccess?: (location: CpmCLocationApiModel) => void;
    @Input()
    public fnOnModalResult?: (modalResult: IBaseCRUModalResultBase<NewEditLocationModalResult>) => void;
    @Input()
    public fnOnDeleteResult?: (location: CpmCLocationApiModel) => void;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'LocationApiModel',
            getModelHumanIdentifier: (model: CpmCLocationApiModel) => model.name ?? '<Unknown location>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCLocationApiModel) =>
                CpmCLocationApiService.deleteLocation(modelToDelete.uuid),

            // Modal config
            modalType: NewEditLocationModalComponent,
            getDialogServiceParameters: NewEditLocationModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCLocationApiModel) =>
                new NewEditLocationModalConfig(
                    model,
                    this._focusParent
                        ? CpmCLocationApiUtils.getAllowedChildrenType(this._focusParent.type)[0]
                        : undefined,
                    this._focusParent?.uuid
                ),

            // Authorization config
            authorizationNew: CpmCLocationApiService.endpointNew(this.FAKE_UUID, this.FAKE_UUID),
            authorizationEdit: CpmCLocationApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCLocationApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID, this.FAKE_UUID)
        });

        this.onModalResult.subscribe(this.onModalResultHandler.bind(this));
        this.onDeleteResult.subscribe(this.onDeleteResultHandler.bind(this));
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    protected override async onRefresh(): Promise<void> {
        const locationsQueryParams: CpmCGetLocationsParams = {};
        if (this._focusParent) locationsQueryParams.parent_uuid = this._focusParent.uuid;

        this.loadingStr = 'Loading locations...';

        const result = await firstValueFrom(
            CpmCLocationApiService.getLocationList(locationsQueryParams, this.paginatorConfig)
        );

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }
    /**
     * On modal result
     * @param modalResult result of the modal
     */
    protected onModalResultHandler(modalResult: IBaseCRUModalResultBase<NewEditLocationModalResult>) {
        this.fnOnModalResult?.(modalResult);
    }
    /**
     * On delete result
     * @param locationModel Delete location model
     */
    protected onDeleteResultHandler(locationModel: CpmCLocationApiModel) {
        this.fnOnDeleteResult?.(locationModel);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Override onDelete to prevent deletion of global locations
     */
    public override async onDelete(row: CpmCLocationApiModel) {
        if (LocationUtils.isGlobalLocation(row)) {
            this.globalsService.messageService.add({
                severity: 'error',
                summary: 'Cannot delete global location',
                detail: 'Global location cannot be deleted'
            });
            return;
        }

        const allLocations = (await firstValueFrom(CpmCLocationApiService.getLocationList({}))).data;

        const directChildren = allLocations.filter((loc) => loc.parent_uuid === row.uuid);

        if (directChildren.length < 1) {
            return await super.onDelete(row);
        }

        const availableLocations = this.filterAvailableReassignLocations(row, allLocations, directChildren);

        const modalResult = await this.openDeletionStrategyModal(row, availableLocations);

        if (!modalResult) return;

        return await this.deleteWithStrategy(row, modalResult);
    }

    private async openDeletionStrategyModal(
        location: CpmCLocationApiModel,
        availableLocations: CpmCLocationApiModel[]
    ): Promise<LocationDeletionStrategyModalResult | null> {
        const config: LocationDeletionStrategyModalConfig = {
            location,
            availableLocations
        };

        const result = await firstValueFrom(
            this.globalsService.dialogService.open(
                LocationDeletionStrategyModalComponent,
                LocationDeletionStrategyModalComponent.getDialogServiceParameters(config)
            ).onClose
        );

        return result;
    }

    /**
     * Filters available locations for reassigning children of a location being deleted
     *
     * Excludes: the location itself, its descendants, and locations incompatible with children types.
     *
     * @param location Location to be deleted
     * @param allLocations Complete list of locations
     * @param directChildren Direct children of the location being deleted
     * @returns Compatible locations for child reassignment
     */
    private filterAvailableReassignLocations(
        location: CpmCLocationApiModel,
        allLocations: CpmCLocationApiModel[],
        directChildren: CpmCLocationApiModel[]
    ): CpmCLocationApiModel[] {
        return allLocations.filter((loc) => {
            if (loc.uuid === location.uuid) return false;
            if (loc.parent_path?.includes(location.uuid)) return false;
            if (directChildren.length < 1) return false;

            const allowedTypes = CpmCLocationApiUtils.getAllowedChildrenType(loc.type);
            return directChildren.every((child) => allowedTypes.includes(child.type));
        });
    }

    private async deleteWithStrategy(
        location: CpmCLocationApiModel,
        modalResult: LocationDeletionStrategyModalResult
    ): Promise<void> {
        const payload: CpmCLocationDeletePayloadApiSchema = {
            target_location_uuid: modalResult.targetLocationUuid
        };

        await firstValueFrom(CpmCLocationApiService.deleteLocation(location.uuid, payload));

        this.globalsService.messageService.add({
            severity: 'success',
            summary: 'Location deleted',
            detail:
                modalResult.targetLocationUuid === null
                    ? 'Location and all descendants deleted successfully'
                    : 'Location deleted and children reassigned successfully'
        });

        this.refresh();
        LocationHierarchyTreeComponent.instance?.refresh();
        this.onDeleteResult.emit({model: location});
    }

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                fnCustomPrefixIcon: (row: CpmCLocationApiModel) => {
                    return LocationUtils.getIconForType(row.type)!;
                }
            },
            {
                field: 'description',
                header: 'Description',
                dataType: ETableColDataType.LONG_TEXT,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            }
        ];

        this.addCreatedColumns();
        this.addCommunicationButtons();

        this.addAccessButtonColumn((row) => {
            if (this.fnOverrideOnAccess) {
                this.fnOverrideOnAccess(row);
                return;
            }

            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([
                CustomerUrlBuilderUtil.customerOrganizationHierarchyLocation(
                    customerUUID!,
                    row.organization_uuid,
                    row.uuid
                )
            ]);
        });
    }
}
